import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { getApiErrorMessage } from '@/utils/dashboard/apiError';
import type { AdminKiosk } from '@/types/dashboard/kiosk';
import type { AdminTransaction } from '@/types/dashboard/transaction';
import type { Vendor } from '@/types/dashboard/vendor';
import {
  TRANSACTION_LIMIT,
  type DateRange,
  useVendor,
  useVendorKiosks,
  useVendorRates,
  useVendorTransactions,
} from './useVendorDetail';

/** Rows shown before "Show more", so long lists stay easy to scan. */
const PAGE_SIZE = 10;

const money = (v: string | number) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const percent = (fraction: string) => `${Math.round(Number(fraction) * 10000) / 100}%`;

const STATUS_BADGE: Record<string, string> = {
  SUCCESS: 'bg-success-subtle text-success',
  PENDING: 'bg-warning-subtle text-warning',
  FAILED: 'bg-danger-subtle text-danger',
  REFUNDED: 'bg-secondary-subtle text-secondary',
};

const ActiveBadge = ({ active }: { active: boolean }) => (
  <span className={`badge rounded-pill ${active ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

function Empty({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="text-center py-5 text-on-surface-variant">
      <i className={`bi ${icon} fs-3 d-block mb-2`} />
      {text}
    </div>
  );
}

function Loading({ text }: { text: string }) {
  return (
    <div className="text-center py-5 text-on-surface-variant">
      <span className="spinner-border spinner-border-sm me-2" />
      {text}
    </div>
  );
}

function Th({ children, end }: { children: ReactNode; end?: boolean }) {
  return <th className={`bg-surface-low border-0 ${end ? 'text-end' : ''}`}>{children}</th>;
}

/** Shows the first PAGE_SIZE rows and a button to reveal more. */
function useShowMore<T>(rows: T[]) {
  const [visible, setVisible] = useState(PAGE_SIZE);
  return {
    rows: rows.slice(0, visible),
    hasMore: rows.length > visible,
    remaining: rows.length - visible,
    showMore: () => setVisible((v) => v + PAGE_SIZE),
  };
}

function ShowMore({ hasMore, remaining, onClick }: { hasMore: boolean; remaining: number; onClick: () => void }) {
  if (!hasMore) return null;
  return (
    <div className="text-center mt-3">
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClick}>
        Show more ({remaining} left)
      </button>
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="position-relative mb-3" style={{ maxWidth: 360 }}>
      <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-on-surface-variant" />
      <input
        type="search"
        className="form-control border-0 rounded-pill bg-surface-low ps-5"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// ── Tab: vendor details ──────────────────────────────────────────────────
function DetailsTab({ v }: { v: Vendor }) {
  const rows: [string, ReactNode][] = [
    ['Vendor name', v.name],
    ['Status', <ActiveBadge key="s" active={v.is_active} />],
    ['Contact person', v.contact_person || '—'],
    ['Email', v.email],
    ['Phone', v.phone],
    ['Address', v.address || '—'],
    ['Onboarded', formatDate(v.created_at)],
  ];
  return (
    <dl className="mb-0 text-body-md" style={{ maxWidth: 720 }}>
      {rows.map(([label, value]) => (
        <div key={label} className="d-flex border-bottom py-3">
          <dt className="text-label-md text-on-surface-variant fw-normal" style={{ width: 180, flexShrink: 0 }}>{label}</dt>
          <dd className="text-on-surface mb-0">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Tab: kiosks ──────────────────────────────────────────────────────────
function KiosksTab({ kiosks, loading }: { kiosks: AdminKiosk[]; loading: boolean }) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = q
    ? kiosks.filter((k) => [k.name, k.location, k.kiosk_serial_number, k.printer_model].some((f) => f.toLowerCase().includes(q)))
    : kiosks;
  const { rows, hasMore, remaining, showMore } = useShowMore(filtered);

  if (loading) return <Loading text="Loading kiosks…" />;
  if (kiosks.length === 0) return <Empty icon="bi-printer" text="No kiosks under this vendor yet." />;

  return (
    <>
      <SearchBox value={search} onChange={setSearch} placeholder="Search kiosk, location, serial or printer" />
      {filtered.length === 0 ? (
        <Empty icon="bi-search" text="No kiosks match your search." />
      ) : (
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr className="text-label-sm text-on-surface-variant">
                <Th>Kiosk</Th>
                <Th>Printer</Th>
                <Th>Rate card</Th>
                <Th>Paper loaded</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((k) => (
                <tr key={k.id} className="text-body-sm text-on-surface">
                  <td>
                    <div className="fw-semibold">{k.name}</div>
                    <div className="text-on-surface-variant">{k.location}</div>
                    <div className="text-on-surface-variant">{k.kiosk_serial_number}</div>
                  </td>
                  <td>{k.printer_model}</td>
                  <td>
                    {k.rate_charge ? (
                      <>
                        <div>B/W ₹{k.rate_charge.bw_rate_per_page} · Colour ₹{k.rate_charge.color_rate_per_page}</div>
                        <div className="text-on-surface-variant">Duplex {percent(k.rate_charge.duplex_extra_charge)} off</div>
                      </>
                    ) : (
                      <span className="text-on-surface-variant">Not set</span>
                    )}
                  </td>
                  <td>
                    {[
                      k.is_a4 && `A4 ${k.a4_count}`,
                      k.is_letter && `Letter ${k.letter_count}`,
                      k.is_legal && `Legal ${k.legal_count}`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td><ActiveBadge active={k.is_active} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ShowMore hasMore={hasMore} remaining={remaining} onClick={showMore} />
    </>
  );
}

// ── Tab: transactions ────────────────────────────────────────────────────
// Local dates as YYYY-MM-DD (what <input type="date"> and the API use)
const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toISO(d);
};

const PRESETS: { label: string; range: () => DateRange }[] = [
  { label: 'All time', range: () => ({ from: '', to: '' }) },
  { label: 'Today', range: () => ({ from: toISO(new Date()), to: toISO(new Date()) }) },
  { label: 'Last 7 days', range: () => ({ from: daysAgo(6), to: toISO(new Date()) }) },
  { label: 'Last 30 days', range: () => ({ from: daysAgo(29), to: toISO(new Date()) }) },
  {
    label: 'This month',
    range: () => {
      const now = new Date();
      return { from: toISO(new Date(now.getFullYear(), now.getMonth(), 1)), to: toISO(now) };
    },
  },
];

function DateFilter({ range, onChange }: { range: DateRange; onChange: (r: DateRange) => void }) {
  return (
    <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
      <div className="btn-group" role="group" aria-label="Quick date ranges">
        {PRESETS.map((p) => {
          const r = p.range();
          const active = r.from === range.from && r.to === range.to;
          return (
            <button
              key={p.label}
              type="button"
              className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => onChange(r)}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <div>
        <label htmlFor="txn-from" className="text-label-sm text-on-surface-variant d-block mb-1">From</label>
        <input
          id="txn-from"
          type="date"
          className="form-control form-control-sm"
          value={range.from}
          max={range.to || undefined}
          onChange={(e) => onChange({ ...range, from: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor="txn-to" className="text-label-sm text-on-surface-variant d-block mb-1">To</label>
        <input
          id="txn-to"
          type="date"
          className="form-control form-control-sm"
          value={range.to}
          min={range.from || undefined}
          onChange={(e) => onChange({ ...range, to: e.target.value })}
        />
      </div>
    </div>
  );
}

function TransactionsTab({
  txns,
  loading,
  range,
  onRangeChange,
}: {
  txns: AdminTransaction[];
  loading: boolean;
  range: DateRange;
  onRangeChange: (r: DateRange) => void;
}) {
  const { rows, hasMore, remaining, showMore } = useShowMore(txns);
  const filtered = Boolean(range.from || range.to);
  const paid = txns.filter((t) => t.payment_status === 'SUCCESS');

  return (
    <>
      <DateFilter range={range} onChange={onRangeChange} />

      {loading && <Loading text="Loading transactions…" />}
      {!loading && txns.length === 0 && (
        <Empty icon="bi-receipt" text={filtered ? 'No transactions in this date range.' : 'No transactions for this vendor yet.'} />
      )}

      {!loading && txns.length > 0 && (
        <div className="text-body-sm text-on-surface-variant mb-3">
          {txns.length} transaction{txns.length === 1 ? '' : 's'} · {paid.length} paid ·{' '}
          {money(paid.reduce((s, t) => s + Number(t.amount), 0))} collected ·{' '}
          {money(paid.reduce((s, t) => s + Number(t.merchant_fee_amount), 0))} commission
        </div>
      )}

      {!loading && txns.length > 0 && (
        <>
      <div className="table-responsive">
        <table className="table align-middle mb-0">
          <thead>
            <tr className="text-label-sm text-on-surface-variant">
              <Th>Transaction</Th>
              <Th>Date</Th>
              <Th>Kiosk</Th>
              <Th>Method</Th>
              <Th end>Amount</Th>
              <Th end>Commission</Th>
              <Th end>Conv. fee</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="text-body-sm text-on-surface">
                <td>
                  <div className="fw-semibold">#{t.id}</div>
                  <div className="text-on-surface-variant">Request {t.print_request}</div>
                </td>
                <td className="text-on-surface-variant">{formatDateTime(t.created_at)}</td>
                <td>{t.kiosk_name}</td>
                <td>{t.payment_method}</td>
                <td className="text-end">{money(t.amount)}</td>
                <td className="text-end">{money(t.merchant_fee_amount)}</td>
                <td className="text-end">{money(t.convenience_fee)}</td>
                <td>
                  <span className={`badge rounded-pill ${STATUS_BADGE[t.payment_status] ?? ''}`}>
                    {t.payment_status.charAt(0) + t.payment_status.slice(1).toLowerCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ShowMore hasMore={hasMore} remaining={remaining} onClick={showMore} />
      {txns.length >= TRANSACTION_LIMIT && (
        <div className="text-body-sm text-on-surface-variant mt-3">Showing the latest {TRANSACTION_LIMIT} payments.</div>
      )}
        </>
      )}
    </>
  );
}

type Tab = 'details' | 'kiosks' | 'rates' | 'transactions';

export function VendorDetailPage() {
  const { vendorId: idParam } = useParams();
  const vendorId = Number(idParam);

  const vendor = useVendor(vendorId);
  const kiosks = useVendorKiosks(vendorId);
  const rates = useVendorRates(vendorId);
  const [range, setRange] = useState<DateRange>({ from: '', to: '' }); // transactions date filter
  const txns = useVendorTransactions(vendorId, range);

  const [tab, setTab] = useState<Tab>('kiosks');

  const back = (
    <Link to={ROUTES.admin.vendors} className="text-label-md text-primary text-decoration-none">
      <i className="bi bi-arrow-left me-1" />
      Back to vendors
    </Link>
  );

  if (!Number.isFinite(vendorId)) {
    return <div className="d-flex flex-column gap-3">{back}<h2 className="h4">Vendor not found</h2></div>;
  }
  if (vendor.isLoading) return <Loading text="Loading vendor…" />;
  if (vendor.isError || !vendor.data) {
    return (
      <div className="d-flex flex-column gap-3">
        {back}
        <div className="alert alert-danger">{getApiErrorMessage(vendor.error, 'Could not load this vendor')}</div>
      </div>
    );
  }

  const v = vendor.data;
  const kioskList = kiosks.data ?? [];
  const rateList = rates.data ?? [];
  const txnList = txns.data ?? [];

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: 'kiosks', label: 'Kiosks', count: kioskList.length },
    { key: 'rates', label: 'Rate cards', count: rateList.length },
    { key: 'transactions', label: 'Transactions', count: txnList.length },
    { key: 'details', label: 'Vendor details' },
  ];

  return (
    <div className="d-flex flex-column gap-3">
      {back}

      <div className="d-flex align-items-center gap-2 flex-wrap">
        <h2 className="text-headline-lg text-on-surface mb-0">{v.name}</h2>
        <ActiveBadge active={v.is_active} />
      </div>

      <div className="card shadow-kiosk-sm">
        <div className="card-body">
          <ul className="nav nav-pills gap-2 mb-3 flex-wrap">
            {tabs.map((t) => (
              <li key={t.key} className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${tab === t.key ? 'active' : 'bg-surface-low text-on-surface-variant'}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                  {t.count !== undefined && <span className="ms-2 badge rounded-pill bg-white text-primary">{t.count}</span>}
                </button>
              </li>
            ))}
          </ul>

          {tab === 'kiosks' && <KiosksTab kiosks={kioskList} loading={kiosks.isLoading} />}

          {tab === 'rates' &&
            (rates.isLoading ? (
              <Loading text="Loading rate cards…" />
            ) : rateList.length === 0 ? (
              <Empty icon="bi-tag" text="No rate cards for this vendor yet." />
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr className="text-label-sm text-on-surface-variant">
                      <Th>Rate card</Th>
                      <Th>B/W</Th>
                      <Th>Colour</Th>
                      <Th>Duplex off</Th>
                      <Th>Commission / page</Th>
                      <Th>Convenience fee</Th>
                      <Th>Kiosks</Th>
                      <Th>Effective from</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateList.map((r) => (
                      <tr key={r.id} className="text-body-sm text-on-surface">
                        <td className="fw-semibold">#{r.id}</td>
                        <td>{money(r.bw_rate_per_page)}</td>
                        <td>{money(r.color_rate_per_page)}</td>
                        <td>{percent(r.duplex_extra_charge)}</td>
                        <td>{money(r.merchant_fees)}</td>
                        <td>{money(r.convenience_fee)}</td>
                        <td>{r.kiosk_count ?? 0}</td>
                        <td className="text-on-surface-variant">{formatDate(r.effective_from)}</td>
                        <td><ActiveBadge active={r.is_active} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

          {tab === 'transactions' && (
            <TransactionsTab txns={txnList} loading={txns.isLoading} range={range} onRangeChange={setRange} />
          )}

          {tab === 'details' && <DetailsTab v={v} />}
        </div>
      </div>
    </div>
  );
}
